import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import { Splide, SplideSlide } from '@splidejs/react-splide'
import '@splidejs/react-splide/css'
import { thumbUrl } from '../utils/photoThumb'

interface ReviewImageSliderProps {
  images: string[]
  /**
   * 사진이 아직 안 그려졌을 때 대신 깔 아주 작은 판 (images와 같은 순서).
   *
   * 한 장을 화면에 그리려면 압축을 풀어야 하고 그 상태가 9MB쯤 된다. 다섯
   * 장이면 45MB라 폰이 다 못 들고 있어서, iOS가 화면 밖 사진의 디코딩을
   * 버린다. 되돌아가면 다시 푸는 사이 칸이 비고 아래 크림색 배경이 드러났다
   * — 그게 '흰색 번쩍'이었다. 같은 그림의 1KB짜리 판을 뒤에 깔아두면 그
   * 틈에도 흐릿한 같은 사진이 보여 번쩍임이 느껴지지 않는다.
   *
   * 아직 안 만들어진 사진은 null이다 — 그때는 예전과 똑같이 동작한다.
   */
  blurs?: (string | null)[]
  initialIndex: number
  onIndexChange: (index: number) => void
}

const getAspectRatio = (w: number, h: number) => (h === 0 ? 1 : w / h)

/**
 * 이 사진이 4:5 프레임을 꽉 채우는가.
 *
 * 채우면 잘라서(cover) 꽉 맞추고, 아니면 통째로 보이게(contain) 넣어
 * 위아래나 좌우에 여백이 생긴다. 그 여백은 앱 배경이어야 한다.
 *
 * 작은 판(blur)을 깔 때도 같은 규칙을 써야 한다. 한때 cover로 박아뒀더니
 * 4:5가 아닌 사진의 여백에 확대된 흐린 사진이 깔려, 앱 배경 대신
 * 모자이크 같은 것이 보였다.
 */
const fitsFrame = (aspect: number): boolean => aspect >= 0.75 && aspect <= 0.85

/**
 * data: 주소로 들어온 JPEG의 가로·세로를 **그리지 않고** 알아낸다.
 *
 * 헤더만 훑는다(1KB). 브라우저에 맡기면 디코딩이 끝날 때까지 기다려야 하고,
 * 그 기다림이 아래 '첫 프레임' 문제를 만든다.
 */
const jpegSize = (dataUri: string): { w: number; h: number } | null => {
  const comma = dataUri.indexOf(',')
  if (comma < 0) return null

  let bytes: Uint8Array
  try {
    const bin = atob(dataUri.slice(comma + 1))
    bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0))
  } catch {
    return null
  }
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return null

  let i = 2
  while (i + 9 < bytes.length) {
    if (bytes[i] !== 0xff) {
      i++ // 채움 바이트
      continue
    }
    const marker = bytes[i + 1]
    if (marker === 0xff) {
      i++
      continue
    }
    // SOF0~SOF15 안에 크기가 있다. DHT(C4)·JPG(C8)·DAC(CC)는 아니다.
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      return { h: (bytes[i + 5] << 8) | bytes[i + 6], w: (bytes[i + 7] << 8) | bytes[i + 8] }
    }
    i += 2 + ((bytes[i + 2] << 8) | bytes[i + 3])
  }

  return null
}

/**
 * 20픽셀짜리 작은 판만 보고 '꽉 채울 사진인가'를 미리 가린다.
 *
 * 왜 미리 가려야 하나 (2026-10-20 실측)
 * ------------------------------------
 * 작은 판은 주소 안에 그림이 들어 있어 **첫 프레임에 바로 그려진다.** 그런데
 * 그때는 비율을 몰라 1:1로 치고 contain으로 깔았다. 잠시 뒤 원본이 와서
 * 비율이 측정되면 cover로 바뀐다. 두 자리가 다르면 그 차이만큼 사진이
 * 갑자기 커진다 — '멀리 있다가 확대되는' 모습이 이것이다.
 *
 * 4:5 사진은 contain과 cover가 같은 자리라 아무 일도 없다. 그래서 거의 모든
 * 사진은 멀쩡하고 4:5가 아닌 한 장만 튄다(기록 187 한식바 도르리: 1440×1919인
 * 첫 장만 93.8% → 100%로 6.6% 커졌고, 나머지 두 장은 0%였다).
 *
 * 반올림을 구간으로 받는다
 * ----------------------
 * 작은 판은 가로가 20픽셀이라 세로가 반올림된다. 20×27은 0.7407로 읽히지만
 * 원본은 0.7504(= 꽉 채우는 쪽)다. 비율 하나로 보면 판정이 뒤집힌다.
 *
 * 그래서 세로의 ±0.5픽셀을 구간으로 잡고, 그 구간이 4:5 범위에 **걸치기만
 * 하면** 꽉 채우는 쪽으로 본다. 실제 사진들로 확인하면 1:1(20×20)·
 * 5:4(20×16)·0.87(20×23)은 그대로 통째로 보이는 쪽으로 갈린다.
 */
const coverByBlur = (w: number, h: number): boolean =>
  w / (h - 0.5) >= 0.75 && w / (h + 0.5) <= 0.85

/**
 * 지금 보는 사진에서 몇 칸까지 원본을 걸어둘지.
 *
 * 왜 전부 걸어두면 안 되나
 * ------------------------
 * 사진 한 장을 화면에 그리려면 압축을 풀어야 하고, 그 상태는 가로세로 픽셀 ×4
 * 바이트다. 이 앱 사진은 보통 1300×1650쯤이라 한 장에 8~10MB다. 다섯 장이면
 * 41MB(실측), 열 장이면 80MB가 넘는다. 폰이 그만큼 못 들고 있어서 iOS가 화면
 * 밖 사진의 압축 해제 결과를 버린다.
 *
 * 그러면 되돌아갈 때 다시 풀어야 하는데 그 사이 칸이 빈다. 실기기 녹화를
 * 60fps로 뜯어보니 정확히 2프레임(33ms) 동안 들어올 사진 자리가 크림색
 * (#FAF6EF, 밝기 245)이었다. 그게 사람 눈에 흰색 번쩍으로 보인 것이다.
 *
 * 양옆 한 칸까지만 원본을 걸면 펼친 메모리가 세 장치(약 25MB)로 묶인다.
 * 장수가 늘어도 그대로라, 사진이 많은 기록일수록 효과가 크다.
 *
 * 먼 칸에는 작은 판(1KB)을 대신 건다. Splide의 lazyLoad처럼 빈 src로 두면
 * 스피너가 돌지만, 작은 판은 주소 안에 그림이 들어 있어 기다림이 없다.
 */
const KEEP_FULL_WITHIN = 1

const ReviewImageSlider: React.FC<ReviewImageSliderProps> = ({
  images,
  blurs,
  initialIndex,
  onIndexChange,
}) => {
  const splideRef = useRef<any>(null)
  // 첫 장이 아니면 오른쪽으로 미는 손짓이 '이전 사진'을 뜻한다.
  // 그때는 화면 왼쪽 가장자리에서 시작해도 뒤로가기에 뺏기면 안 된다.
  const [index, setIndex] = useState(initialIndex)
  /*
   * 지금 가고 있는 칸. 이동이 시작될 때 먼저 바뀐다.
   *
   * '정착한 칸'과 따로 두는 이유: 창을 하나로 두고 이동 시작에 옮겼더니,
   * 아직 화면에 보이는 칸이 창 밖으로 밀려 작은 판으로 떨어졌다. 보고 있던
   * 사진이 눈앞에서 모자이크가 됐다. 올리는 것은 미리, 내리는 것은 도착한
   * 뒤에 — 그래서 둘을 나눠 두 창의 합집합을 원본으로 유지한다.
   */
  const [destIndex, setDestIndex] = useState(initialIndex)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [aspects, setAspects] = useState<Record<number, number>>({})

  /*
   * 작은 판에서 읽어낸 것. 그리기 전에 끝나므로 첫 프레임부터 쓸 수 있다.
   */
  const fromBlur = useMemo(
    () =>
      (blurs ?? []).map((b) => {
        const size = b ? jpegSize(b) : null
        return size ? { aspect: getAspectRatio(size.w, size.h), cover: coverByBlur(size.w, size.h) } : null
      }),
    [blurs]
  )

  /** 잰 값이 있으면 그걸, 없으면 작은 판의 판정을, 그것도 없으면 예전대로. */
  const coverAt = (i: number): boolean => {
    const measured = aspects[i]
    if (measured !== undefined) return fitsFrame(measured)
    return fromBlur[i]?.cover ?? fitsFrame(1)
  }
  const aspectAt = (i: number): number => aspects[i] ?? fromBlur[i]?.aspect ?? 1

  // 핀치 줌 상태
  const isPinchingRef = useRef(false)
  const initDistRef = useRef(0)
  const currentImgRef = useRef<HTMLImageElement | null>(null)

  const dist = (t: TouchList) => {
    const dx = t[0].clientX - t[1].clientX
    const dy = t[0].clientY - t[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  // 전체 wrapper에서 네이티브 이벤트로 핀치 가로채기
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        e.preventDefault()
        e.stopImmediatePropagation()
        isPinchingRef.current = true
        initDistRef.current = dist(e.touches)

        // 현재 활성 슬라이드의 img 찾기
        const activeSlide = wrapper.querySelector('.splide__slide.is-active') || wrapper.querySelector('.splide__slide')
        const img = activeSlide?.querySelector('img') as HTMLImageElement | null
        currentImgRef.current = img

        if (img) {
          const rect = img.getBoundingClientRect()
          const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2
          const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2
          const originX = ((midX - rect.left) / rect.width) * 100
          const originY = ((midY - rect.top) / rect.height) * 100
          img.style.transformOrigin = `${originX}% ${originY}%`
        }
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      // 두 손가락이 하나라도 있으면 핀치 모드
      if (e.touches.length >= 2) {
        if (!isPinchingRef.current) {
          // 한 손가락으로 시작했다가 두 번째가 추가된 경우
          isPinchingRef.current = true
          initDistRef.current = dist(e.touches)

          const activeSlide = wrapper.querySelector('.splide__slide.is-active') || wrapper.querySelector('.splide__slide')
          const img = activeSlide?.querySelector('img') as HTMLImageElement | null
          currentImgRef.current = img

          if (img) {
            const rect = img.getBoundingClientRect()
            const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2
            const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2
            const originX = ((midX - rect.left) / rect.width) * 100
            const originY = ((midY - rect.top) / rect.height) * 100
            img.style.transformOrigin = `${originX}% ${originY}%`
          }

          // Splide 스와이프 강제 취소
          const sp = splideRef.current?.splide
          if (sp && sp.Components?.Drag) {
            try { sp.Components.Drag.cancel() } catch (_) {}
          }
        }

        e.preventDefault()
        e.stopImmediatePropagation()

        const d = dist(e.touches)
        const scale = Math.max(1, d / initDistRef.current)
        if (currentImgRef.current) {
          currentImgRef.current.style.transform = `scale(${scale})`
        }
      } else if (isPinchingRef.current) {
        // 핀치 중 손가락 하나가 빠진 경우 — 움직임 무시
        e.preventDefault()
        e.stopImmediatePropagation()
      }
    }

    const onTouchEnd = () => {
      if (!isPinchingRef.current) return
      isPinchingRef.current = false
      const img = currentImgRef.current
      if (img) {
        img.style.transition = 'transform 0.25s ease-out'
        img.style.transform = 'scale(1)'
        setTimeout(() => {
          if (img) img.style.transition = ''
        }, 250)
      }
      currentImgRef.current = null
    }

    wrapper.addEventListener('touchstart', onTouchStart, { capture: true, passive: false })
    wrapper.addEventListener('touchmove', onTouchMove, { capture: true, passive: false })
    wrapper.addEventListener('touchend', onTouchEnd, { capture: true })
    wrapper.addEventListener('touchcancel', onTouchEnd, { capture: true })

    return () => {
      wrapper.removeEventListener('touchstart', onTouchStart, { capture: true })
      wrapper.removeEventListener('touchmove', onTouchMove, { capture: true })
      wrapper.removeEventListener('touchend', onTouchEnd, { capture: true })
      wrapper.removeEventListener('touchcancel', onTouchEnd, { capture: true })
    }
  }, [])

  /*
   * 사진 파일은 처음부터 전부 받아둔다.
   *
   * 멀리 있는 칸에는 원본 대신 작은 판을 걸어 펼친 메모리를 묶어두는데,
   * 그러면 그 사진의 주소를 아무도 요청하지 않아 파일 자체가 안 받아진다.
   * 넘기는 순간에야 받기 시작하니 모자이크가 오래 남았다 — 줄이려던 것은
   * 메모리인데 받아오기까지 같이 미뤄버린 것이다.
   *
   * 그래서 파일만 따로 미리 받아 둔다. fetch는 받아만 두고 압축을 풀지
   * 않으므로 메모리에는 영향이 없다(한 장 200KB). 나중에 칸이 가까워져
   * 원본으로 올라올 때는 이미 받아져 있어 바로 압축만 풀면 된다.
   *
   * 실패는 무시한다 — 못 받아도 그때 가서 받으면 되고, 예전과 같아질 뿐이다.
   */
  useEffect(() => {
    if (!images.length) return
    const ac = new AbortController()

    images.forEach((url) => {
      if (!url || url.startsWith('data:')) return
      fetch(url, { signal: ac.signal, mode: 'no-cors', credentials: 'omit' }).catch(
        () => {}
      )
    })

    return () => ac.abort()
  }, [images])

  /*
   * 비율은 원본을 기다리지 말고 썸네일로 먼저 잰다.
   *
   * 왜 필요한가
   * ----------
   * 밑에 깔아두는 판과 원본이 **서로 다른 규칙으로 놓이고 있었다.** 비율을
   * 모르는 동안에는 1:1로 치고 contain 으로 깔다가, 원본이 와서 비율이
   * 측정되면 cover 로 바뀐다. 두 상태의 크기가 다르면 그 차이만큼 사진이
   * 갑자기 커진다 — '멀리 있다가 확대되는' 모습이 이것이다.
   *
   * 4:5 사진은 contain 과 cover 가 같은 자리라 아무 일도 없다. 그래서 거의
   * 모든 사진은 멀쩡하고, 4:5 가 아닌 한 장만 튄다. 실측(기록 187, 한식바
   * 도르리): 1440×1919(0.7504)인 첫 장만 93.8% → 100% 로 6.6% 커졌고,
   * 나머지 두 장(0.80)은 0% 였다. 보고 있는 칸만 그 과정을 지켜보게 되므로
   * '첫 사진만 그런다'로 보인다.
   *
   * 썸네일은 목록이 이미 받아둔 33KB 짜리라 기다릴 것이 없고, 비율은 원본과
   * 같다(480×640 ↔ 1440×1919). 작은 판(20px)으로는 안 된다 — 반올림하면
   * 0.74 가 되어 4:5 판정이 뒤집힌다.
   *
   * 원본이 와서 잰 값이 더 정확하므로 그쪽이 이긴다. 여기서는 아직 모르는
   * 칸만 채운다.
   */
  useEffect(() => {
    if (!images.length) return
    let alive = true

    const probes = images.map((url, slideIndex) => {
      if (!url || url.startsWith('data:')) return null

      const probe = new Image()
      probe.onload = () => {
        if (!alive || probe.naturalWidth < 100) return
        setAspects((prev) =>
          prev[slideIndex] !== undefined
            ? prev
            : {
                ...prev,
                [slideIndex]: getAspectRatio(probe.naturalWidth, probe.naturalHeight),
              }
        )
      }
      // 썸네일이 없는 사진(404)은 그냥 넘어간다 — 예전처럼 원본을 기다린다
      probe.src = thumbUrl(url)
      return probe
    })

    return () => {
      alive = false
      probes.forEach((probe) => {
        if (probe) probe.onload = null
      })
    }
  }, [images])

  /*
   * 밖에서 인덱스를 바꿨을 때만 따라간다.
   *
   * 손으로 넘기면 onIndexChange → 부모 상태 변경 → initialIndex 변경으로
   * 돌아오는데, 그때 다시 go()를 부르면 방금 끝난 이동을 한 번 더 시킨다.
   * 이미 그 자리에 있으면 아무것도 하지 않는다.
   */
  useEffect(() => {
    const splide = splideRef.current
    if (!splide || initialIndex < 0) return
    if (splide.splide?.index === initialIndex) return

    setIndex(initialIndex)
    setDestIndex(initialIndex)
    splide.go(initialIndex)
  }, [initialIndex])

  /*
   * 이동이 '끝났을 때'가 아니라 '시작할 때' 창을 옮긴다.
   *
   * 끝난 뒤에 옮기면 다음 칸이 원본으로 올라올 시간이 이동 사이의 빈 틈밖에
   * 없다. 빠르게 연속으로 넘기면 그 틈이 없어 모자이크인 채로 도착한다.
   * 시작할 때 옮기면 넘어가는 애니메이션(400ms)만큼을 더 번다.
   */
  const handleSplideMoveStart = (_splide: any, newIndex: number) => {
    setDestIndex(newIndex)
  }

  /*
   * 첫 사진에서 오른쪽, 마지막 사진에서 왼쪽으로 밀어도 끌려가지 않게 한다.
   *
   * Splide는 끝을 넘겨도 1/5 저항으로 끌려가게 두는데(FRICTION=5), 그 저항은
   * 코드에 박힌 상수라 옵션으로 못 끈다. 끌려간 자리에는 보여줄 사진이 없어
   * 뒤에 깔린 것이 그대로 드러난다.
   *
   * 그래서 끄는 중에 매번 위치를 확인해, 한계를 넘었으면 한계에 도로 붙인다.
   * 넘어간 적이 없는 것처럼 되어 손을 떼도 되돌아오는 움직임이 없다.
   */
  const handleDragging = (splide: any) => {
    const move = splide?.Components?.Move
    if (!move || !move.exceededLimit()) return

    move.translate(move.getLimit(move.exceededLimit(true)))
  }

  const handleSplideMove = (splide: any) => {
    setIndex(splide.index)
    setDestIndex(splide.index)
    onIndexChange(splide.index)
  }

  const handleImageLoad = useCallback((index: number, e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    // 작은 판이 걸려 있는 동안에도 onLoad는 불린다. 비율은 같지만 20px짜리라
    // 반올림 오차로 판정이 흔들릴 수 있어 원본일 때만 기록한다.
    if (img.naturalWidth < 100) return
    setAspects(prev => ({ ...prev, [index]: getAspectRatio(img.naturalWidth, img.naturalHeight) }))
  }, [])

  if (!images || images.length === 0) return null

  return (
    <div
      ref={wrapperRef}
      className="review-image-slider w-full shrink-0 relative overflow-hidden"
      /*
       * 마지막 안전망은 칸이 아니라 '틀'에 깐다.
       *
       * 칸 배경에 깔아봤자 소용이 없었다. 실기기 녹화를 원본 해상도로 잘라
       * 보니, 들어오는 사진이 240픽셀만 그려지고 나머지는 통째로 앱 배경색
       * (#FAF6EF)이었다. 240은 WebKit이 화면을 쪼개 그리는 타일 크기(256)다.
       * 즉 안 그려진 것은 '사진'이 아니라 '그 칸의 타일'이고, 타일이 없으면
       * 거기 얹은 배경도 같이 없다. 칸에 뭘 깔든 닿지 않는다.
       *
       * 틀은 내내 화면에 있어 이미 그려져 있다. 여기에 깔면 칸의 타일이
       * 늦어도 그 자리에 흐릿한 사진이 있어, 흰색이 드러나지 않는다.
       */
      style={{
        aspectRatio: '4 / 5',
        ...(blurs?.[index]
          ? {
              backgroundImage: `url(${blurs[index]})`,
              // 지금 보는 사진과 같은 맞춤. cover로 박아두면 4:5가 아닌
              // 사진의 여백까지 흐린 사진이 덮어 앱 배경이 안 보인다.
              backgroundSize: coverAt(index) ? 'cover' : 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : null),
      }}
      /*
       * 첫 사진에서는 오른쪽으로 밀어도 넘어갈 이전 사진이 없다.
       * 그 손짓은 뒤로가기로 넘긴다 — 화면 어디서 시작하든.
       *
       * 두 번째 사진부터는 반대다. 오른쪽으로 미는 것이 '이전 사진'이라
       * 뒤로가기가 가져가면 안 된다.
       */
      {...(index > 0
        ? { 'data-swipe-back-block': '' }
        : { 'data-swipe-back-allow': '' })}
    >
      {/* Splide 내부 체인을 4:5 프레임 전체 높이로 채워야 슬라이드 안 flex items-center가
          실제로 동작해서 1:1·가로 이미지가 프레임 세로 중앙에 온다 */}
      {/* shrink-0이 없으면 4:5가 지켜지지 않는다.
          이 프레임은 세로 flex(ReviewPhotoDetail의 스크롤 영역) 안의 아이템이라
          기본값 flex-shrink:1로 줄어들 수 있다. 본문이 길어 내용이 넘치면 브라우저가
          이 칸부터 눌러 줄이는데, 줄어드는 한계(min-height:auto)가 사진의 최소 높이라
          1:1 사진이면 프레임이 딱 사진 크기까지 찌그러져 상하 여백이 사라졌다.
          본문이 짧은 리뷰는 넘치지 않아 멀쩡해 보여서, 사진 장수 문제로 오해하기 쉽다. */}
      <style>{`
        .review-image-slider .splide,
        .review-image-slider .splide__track,
        .review-image-slider .splide__list,
        .review-image-slider .splide__slide { height: 100%; }
      `}</style>
      <Splide
        ref={splideRef}
        options={{
          type: 'slide',
          perPage: 1,
          perMove: 1,
          pagination: false,
          arrows: false,
          start: initialIndex,
          width: '100%',
          height: '100%',
          speed: 400,
          easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
          rewind: false,
          gap: '0',
          drag: true,
          snap: true,
        }}
        onDragging={handleDragging}
        onMove={handleSplideMoveStart}
        onMoved={handleSplideMove}
        className="w-full h-full"
        style={{ height: '100%' }}
      >
        {images.map((image, slideIndex) => {
          const aspect = aspectAt(slideIndex)
          const isCover = coverAt(slideIndex)
          const isTall = aspect < 0.7
          const blur = blurs?.[slideIndex] ?? null

          // 멀리 있는 칸은 원본 대신 작은 판을 건다 (작은 판이 없으면 원본 유지).
          // 정착한 칸과 가고 있는 칸, 두 창 중 하나에만 들어도 원본을 유지한다.
          const near =
            Math.abs(slideIndex - index) <= KEEP_FULL_WITHIN ||
            Math.abs(slideIndex - destIndex) <= KEEP_FULL_WITHIN
          const src = near || !blur ? image : blur

          /*
           * 원본을 기다리는 동안 깔아둘 그림.
           *
           * 피드 썸네일과 뷰어는 서로 다른 파일을 쓴다 — 피드는
           * /reviews/thumbs/..., 뷰어는 /reviews/... 이다. 그래서 사진을
           * 누르는 순간 원본(196KB)은 한 번도 받은 적이 없고, 내려받아
           * 압축을 푸는 동안 20픽셀짜리 작은 판이 보였다. 그게 '한 번
           * 뿌옇게 됐다가 뜨는' 모습이다.
           *
           * 썸네일(480×600, 33KB)은 피드가 이미 받아놨다. 받을 것이 없으니
           * 바로 그려진다. 선명하진 않아도 무슨 사진인지 알아볼 수 있다.
           *
           * 두 장을 겹쳐 깐다 — 썸네일이 위, 작은 판이 아래. 썸네일 파일이
           * 없는 사진(404)은 그 층이 안 그려지고 작은 판이 드러난다.
           *
           * 가까운 칸에만 깐다. 펼친 썸네일이 한 장에 1.1MB라, 멀리 있는
           * 칸까지 깔면 창을 좁혀 아낀 메모리를 도로 까먹는다.
           */
          const under: string[] = []
          if (near && image && !image.startsWith('data:')) {
            under.push(`url(${thumbUrl(image)})`)
          }
          if (blur) under.push(`url(${blur})`)
          const fit = isCover ? 'cover' : 'contain'

          return (
            /*
             * 작은 판은 슬라이드 칸(li) 자체에 깐다.
             *
             * 처음에는 img를 감싼 안쪽 div에 깔았는데 소용이 없었다. 녹화를
             * 프레임으로 뜯어보니 비는 것은 사진이 아니라 '칸'이었다 —
             * 넘기는 도중 들어와야 할 칸이 아직 안 그려진 채로 드러나고,
             * 그 틈으로 아래 크림색(#F8F4EE) 배경이 보였다. 안쪽 div는
             * 그 칸 안에 있으니 칸이 비면 같이 비어 닿지 못했다.
             *
             * 칸에 깔면 사진이 아직 없어도 흐릿한 같은 그림이 자리를 채운다.
             */
            <SplideSlide
              key={slideIndex}
              style={{
                width: '100%',
                height: '100%',
                /*
                 * 가까운 칸은 각자 층으로 올린다.
                 *
                 * Splide는 칸들을 담은 '줄' 하나를 통째로 옮긴다. 그 줄은
                 * 사진 장수만큼 넓어서(다섯 장이면 4420px) WebKit이 타일로
                 * 쪼개 그리고, 옮길 때마다 새로 보이는 타일을 그때그때
                 * 그린다. 그 지연이 위에서 본 240픽셀짜리 잘린 사진이다.
                 *
                 * 칸마다 층을 주면 각 층이 한 번 그려진 뒤 그대로 재사용된다.
                 * 옮기는 것은 다 그려진 층을 미는 일이라 다시 그릴 게 없다.
                 * 멀리 있는 칸까지 올리면 층이 장수만큼 늘어나므로 창 안쪽만.
                 */
                ...(near
                  ? { transform: 'translateZ(0)', backfaceVisibility: 'hidden' as const }
                  : null),
                ...(under.length
                  ? {
                      backgroundImage: under.join(', '),
                      // 사진과 같은 맞춤을 써야 여백이 앱 배경으로 남는다
                      backgroundSize: under.map(() => fit).join(', '),
                      backgroundPosition: under.map(() => 'center').join(', '),
                      backgroundRepeat: under
                        .map(() => 'no-repeat')
                        .join(', '),
                    }
                  : null),
              }}
            >
              <div className="w-full h-full flex items-center justify-center overflow-hidden">
                <img
                  src={src}
                  alt={`기록 이미지 ${slideIndex + 1}`}
                  className={isCover ? 'w-full h-full' : isTall ? 'h-full' : 'w-full'}
                  style={{ objectFit: isCover ? 'cover' : 'contain', objectPosition: 'center' }}
                  onLoad={(e) => handleImageLoad(slideIndex, e)}
                  draggable={false}
                />
              </div>
            </SplideSlide>
          )
        })}
      </Splide>
    </div>
  )
}

export default ReviewImageSlider
